import type { MfaMethodLocal } from '../api/types/TenantLocal';
import type { PrincipalIdentifierLocal } from '../api/types/PlatformLocal';

/**
 * Fuente única de verdad de la política de seguridad (2FA) de un tenant, compartida por TODOS los
 * editores: el del propio tenant (`SecurityPolicyEditor`), el drill-down platform de un tenant
 * (`EditTenantSecuritySection`) y la política de la plataforma misma (`PlatformSecurityEditor`).
 *
 * Antes cada editor tenía su propia copia del catálogo de métodos y de la regla de guardado, y el
 * editor del propio tenant NO validaba el invariante anti-lockout (DEC-RBAC-087) → un tenant podía
 * guardar una config 2FA sin método universal que el drill-down platform sí bloqueaba (riesgo de
 * lockout). Centralizar cierra esa brecha: los tres consumen la MISMA regla.
 */

/** Draft de política de seguridad: 2FA on/off + métodos de 2º factor permitidos (aplican con 2FA prendido). */
export interface SecurityDraft {
    /** 2FA prendido/apagado a nivel tenant. */
    mfaEnabled: boolean;
    /** Métodos de 2º factor permitidos (TOTP / EMAIL / SMS). Aplica solo con 2FA prendido. */
    methodsAllowed: MfaMethodLocal[];
}

// DEC-095 / DEC-RBAC-084: el tenant elige qué métodos de 2º factor permite a sus usuarios (EMAIL / TOTP / SMS).
// El backend exige al menos 1 método; el front lo replica para no mandar PUT vacíos.
export const MFA_METHODS: { value: MfaMethodLocal; label: string; hint: string }[] = [
    {
        value: 'EMAIL',
        label: 'Correo electrónico',
        hint: 'Código de un solo uso enviado al correo del usuario.',
    },
    {
        value: 'SMS',
        label: 'SMS',
        hint: 'Código de un solo uso enviado por SMS al teléfono del usuario.',
    },
    {
        value: 'TOTP',
        label: 'App de autenticación (TOTP)',
        hint: 'Código rotativo de una app como Google Authenticator o Authy.',
    },
];

/**
 * DEC-RBAC-087 (invariante anti-lockout): con 2FA prendido, debe haber al menos un método UNIVERSAL —
 * TOTP (agnóstico) o el OTP del identificador PRINCIPAL del tenant (todos lo tienen). El OTP del
 * SECUNDARIO no puede ser el único (dejaría afuera a usuarios sin el secundario). `principalIdentifier`
 * ausente ⇒ no se puede evaluar el invariante (fallback histórico: solo exige ≥1 método).
 */
export function hasUniversalMethod(
    methodsAllowed: MfaMethodLocal[],
    principalIdentifier: PrincipalIdentifierLocal,
): boolean {
    const principalOtp: MfaMethodLocal = principalIdentifier === 'PHONE' ? 'SMS' : 'EMAIL';
    return methodsAllowed.includes('TOTP') || methodsAllowed.includes(principalOtp);
}

/**
 * Con 2FA prendido se exige al menos 1 método (el backend lo rechaza igual) Y, si se conoce el
 * identificador principal, al menos un método universal (DEC-RBAC-087: el único OTP no puede ser el del
 * identificador secundario). Con 2FA apagado la regla no aplica.
 */
export function securityCannotSave(
    draft: SecurityDraft,
    principalIdentifier?: PrincipalIdentifierLocal,
): boolean {
    if (!draft.mfaEnabled) return false;
    if (draft.methodsAllowed.length === 0) return true;
    if (principalIdentifier && !hasUniversalMethod(draft.methodsAllowed, principalIdentifier)) {
        return true;
    }
    return false;
}

/** Etiqueta del OTP del identificador principal (para los mensajes anti-lockout). */
export function principalOtpLabel(principalIdentifier: PrincipalIdentifierLocal): string {
    return principalIdentifier === 'PHONE' ? 'SMS' : 'correo';
}
